import { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { PageHelpModal, PageHelpContent } from './PageHelpModal';
import { PAGE_HELP_CONTENT } from '../../data/pageHelpContent';

interface PageHelpButtonProps {
  className?: string;
  /** Hide while a dialog is open (PT-AUDIT-0147/0149/0171). */
  hidden?: boolean;
}

export function PageHelpButton({ className = '', hidden = false }: PageHelpButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Get help content for current route
  const getHelpContent = (): PageHelpContent | null => {
    const pathname = location.pathname || '/';

    // 1. Try exact match first
    if (PAGE_HELP_CONTENT[pathname]) {
      return PAGE_HELP_CONTENT[pathname];
    }

    // 2. Try parent paths (e.g., /finance/invoices -> /finance)
    const pathParts = pathname.split('/').filter(Boolean);
    const firstSegment = pathParts[0] || '';
    while (pathParts.length > 0) {
      const parentPath = '/' + pathParts.join('/');
      if (PAGE_HELP_CONTENT[parentPath]) {
        return PAGE_HELP_CONTENT[parentPath];
      }
      pathParts.pop();
    }

    // 3. Resolve domain-specific primer based on top-level route segment
    if (['crm', 'customers', 'companies', 'travelers', 'suppliers', 'partners', 'leads'].includes(firstSegment)) {
      if (PAGE_HELP_CONTENT['domain:crm']) return PAGE_HELP_CONTENT['domain:crm'];
    }
    if (['finance', 'invoices', 'payments', 'banking', 'commissions', 'ar-aging', 'fora'].includes(firstSegment)) {
      if (PAGE_HELP_CONTENT['domain:finance']) return PAGE_HELP_CONTENT['domain:finance'];
    }
    if (['analytics', 'reports', 'ai', 'ai-assistant', 'propeller'].includes(firstSegment)) {
      if (PAGE_HELP_CONTENT['domain:intelligence']) return PAGE_HELP_CONTENT['domain:intelligence'];
    }
    if (['admin', 'settings', 'users', 'permissions', 'integrations', 'manuals', 'system-health'].includes(firstSegment)) {
      if (PAGE_HELP_CONTENT['domain:admin']) return PAGE_HELP_CONTENT['domain:admin'];
    }
    if (['ops', 'aoc', 'ucc', 'pnr', 'pnt', 'bookings', 'trips', 'cruises', 'rtw', 'tasks', 'deadlines', 'queues', 'sabre', 'amadeus', 'itinerary', 'quotes', 'proposals'].includes(firstSegment)) {
      if (PAGE_HELP_CONTENT['domain:operations']) return PAGE_HELP_CONTENT['domain:operations'];
    }

    // 4. Return 5-Domain platform guide fallback
    return PAGE_HELP_CONTENT['default'] || null;
  };

  const helpContent = getHelpContent();

  // Don't render if no help content available
  if (!helpContent) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        data-floating-launcher="help"
        data-launcher-hidden={hidden ? 'true' : undefined}
        tabIndex={hidden ? -1 : undefined}
        className={`fixed bottom-[4.5rem] right-[18px] z-40 p-3 max-md:bottom-[0.625rem] max-md:right-[6.75rem] max-md:p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all group ${className}`}
        title="Page Help"
        aria-label="Open page help"
      >
        <HelpCircle className="w-5 h-5" />
        <span className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Page Help
        </span>
      </button>

      <PageHelpModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        content={helpContent}
      />
    </>
  );
}
